/* eslint-disable jsx-a11y/no-noninteractive-element-to-interactive-role */

/* eslint-disable jsx-a11y/no-noninteractive-element-interactions */
import { Builder } from '@builder.io/react';
import { builder } from '@builder.io/react';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { ThunkDispatch } from 'redux-thunk';
import { twMerge } from 'tailwind-merge';

import ChevronDown from '@videoblocks/react-icons/ChevronDown';

import { loadBuilderDataOrFallback } from '../../../app/Search/actions/SearchActions';
import { selectSearchBuilderData } from '../../../app/Search/selectors/searchSelectors';
import { Text } from '../../storyblocks-design-system/components/atoms/Text/Text';
import { generateFAQSchema } from '../../utils/structured-data/faqSchemaGenerator';
import StructuredData from '../elements/StructuredData';

const Faq = ({
  urlSlug,
  modelName = 'category-search-page',
}: {
  urlSlug?: string;
  modelName?: string;
}) => {
  const dispatch: ThunkDispatch<any, any, any> = useDispatch();
  const builderContent = useSelector(selectSearchBuilderData);
  const [accordion, setAccordion] = useState({});

  if (!urlSlug) return null;

  // Generate structured data for FAQ
  const faqStructuredData = useMemo(() => {
    if (!builderContent.faqItems || builderContent.faqItems.length === 0)
      return null;

    return generateFAQSchema({
      faqItems: builderContent.faqItems,
      title: 'Frequently Asked Questions',
      organizationName: 'Storyblocks',
    });
  }, [builderContent.faqItems]);

  useEffect(() => {
    if (urlSlug) {
      // If urlSlug is provided, fetch the content
      dispatch(
        loadBuilderDataOrFallback(urlSlug, modelName, {
          faqItems: [],
          urlSlug,
        })
      );
    }
  }, [urlSlug, modelName]);

  const handleOnToggle = (section: string) => {
    setAccordion((prev) => ({
      ...prev,
      [section]: !prev[section],
    }));
  };

  const handleKeyDown = (e: React.KeyboardEvent, section: string) => {
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      handleOnToggle(section);
    }
  };

  if (builderContent?.faqItems?.length === 0) return null;

  return (
    <>
      {faqStructuredData && <StructuredData data={faqStructuredData} />}
      <div className="font-polysans flex flex-col lg:flex-row w-full gap-8 px-6 py-16 md:py-24 md:px-12 lg:px-24">
        <div className="flex-auto lg:max-w-120 lg:w-full">
          <Text fontFamily="polysans" fontWeight="semibold" fontSize="5xl">
            <>
              <span className="lg:block">Frequently </span>Asked Questions
            </>
          </Text>
        </div>
        <ul className="flex-auto space-y-6">
          {builderContent.faqItems.length > 0
            ? builderContent.faqItems.map(
                ({ faqAnswer, faqQuestion }, index) => (
                  <li
                    className={twMerge(
                      'bg-gray-200 rounded-3xl border-gray-300 border-2',
                      'flex flex-col w-full p-8 text-left',
                      'cursor-pointer transition-colors duration-150 rounded-3xl',
                      'focus-visible:outline-white focus-visible:ring-blue-600 focus-visible:ring-2',
                      'active:bg-gray-300 active:border-gray-500',
                      'touch-manipulation'
                    )}
                    onClick={() => handleOnToggle(`faq_${index}`)}
                    onKeyDown={(e) => handleKeyDown(e, `faq_${index}`)}
                    aria-controls={`faq-content-${index}`}
                    id={`faq-question-${index}`}
                    key={`faq_${index}`}
                    role="button"
                    tabIndex={0}
                  >
                    <div className="flex w-full justify-between items-center">
                      <Text
                        className="text-gray-1000 my-0 pr-4"
                        fontFamily="polysans"
                        fontSize="2xl"
                        fontWeight="semibold"
                        asChild
                      >
                        <h3>{faqQuestion}</h3>
                      </Text>
                      <div className="flex flex-none justify-center items-center w-8 h-8 bg-yellow-300 rounded-full border-2 border-blue-900 transition-transform duration-200">
                        <ChevronDown
                          className={twMerge(
                            'h-4 w-4 transition-transform duration-200 ease-in-out',
                            accordion[`faq_${index}`] && 'transform rotate-180'
                          )}
                          aria-hidden="true"
                        />
                      </div>
                    </div>
                    <div
                      id={`faq-content-${index}`}
                      className={twMerge(
                        accordion[`faq_${index}`] ? 'max-h-1000px' : 'max-h-0',
                        'overflow-hidden transition-all duration-200 ease-in-out',
                        accordion[`faq_${index}`] && 'pt-4'
                      )}
                      role="region"
                      aria-live="polite"
                      aria-labelledby={`faq-question-${index}`}
                    >
                      <Text
                        className="text-gray-800 my-0"
                        fontFamily="polysans"
                        fontWeight="regular"
                        fontSize="base"
                      >
                        {faqAnswer}
                      </Text>
                    </div>
                  </li>
                )
              )
            : null}
        </ul>
      </div>
    </>
  );
};

export default Faq;

Builder.registerComponent(Faq, {
  name: 'Faq SB',
  inputs: [],
});
